import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import changeLogService from "services/change-log-service";
import crudsService from "services/cruds-service";
import breakpoints from "assets/theme/base/breakpoints";

import Card from "@mui/material/Card";
import CircularProgress from "@mui/material/CircularProgress";
import Icon from "@mui/material/Icon";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDAvatar from "components/MDAvatar";
import AppBar from "@mui/material/AppBar";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";


import DataTable from "e4acomponents/Tables/DataTable";
import ImageModal from "e4acomponents/ImageModal";
import formatPartCode from "helper/formatPartCode";

export default function RecentChanges() {

    const [partCodeChanges, setPartCodeChanges] = useState([]);
    const [partCodeTableData, setPartCodeTableData] = useState([]);
    const [inventoryChanges, setInventoryChanges] = useState([]);
    const [inventoryTableData, setInventoryTableData] = useState([]);
    const [avatars, setAvatars] = useState([]);

    const [isVisible, setIsVisible] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [tabValue, setTabValue] = useState(0);
    const [tabsOrientation, setTabsOrientation] = useState("horizontal");
    const [openImageModal, setOpenImageModal] = useState(false);
    const [imageUrl, setImageUrl] = useState("");

    const navigate = useNavigate();

    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                // Fetch recent changes from the server
                const partCodeChanges = await changeLogService.getTypeChanges("parts", 10);
                const inventoryChanges = await changeLogService.getTypeChanges("inventoryitems", 10);

                const partCodeChangesData = partCodeChanges.data.attributes;
                const inventoryChangesData = inventoryChanges.data.attributes;

                const images = await fetchImages([...partCodeChangesData, ...inventoryChangesData]);

                setInventoryChanges(inventoryChangesData);
                setPartCodeChanges(partCodeChangesData);
                setAvatars(images);
            } catch (error) {
                console.error("Error fetching recent changes:", error);
            }
        })();
    }, []);

    useEffect(() => {
        setIsVisible(true);
    }, []);

    useEffect(() => {
        setPartCodeTableData(getRows(partCodeChanges, 1));
        setInventoryTableData(getRows(inventoryChanges, 2));
    }, [avatars]);

    useEffect(() => {
        if (partCodeTableData.length > 0) {
            setIsLoading(false);
        }
    }, [partCodeTableData]);

    useEffect(() => {
        // A function that sets the orientation state of the tabs.
        function handleTabsOrientation() {
            return window.innerWidth < breakpoints.values.sm
                ? setTabsOrientation("vertical")
                : setTabsOrientation("horizontal");
        }

        /** 
         The event listener that's calling the handleTabsOrientation function when resizing the window.
        */
        window.addEventListener("resize", handleTabsOrientation);

        // Call the handleTabsOrientation function to set the state with the initial value.
        handleTabsOrientation();

        // Remove event listener on cleanup
        return () => window.removeEventListener("resize", handleTabsOrientation);
    }, [tabsOrientation]);

    // Fetches the user's profile images from the server
    const fetchImages = async (changeLogs) => {
        let images = {};
        for (let i = 0; i < changeLogs.length; i++) {
            const change = changeLogs[i]
            if (!images[change.user._id] && !avatars[change.user._id]) {
                try {
                    images[change.user._id] = await crudsService.getImage(change.user.profile_image);
                } catch (err) {
                    images[change.user._id] = change.user.profile_image;
                }
            }
        }
        return images;
    }

    // Handles the tab value
    const handleSetTabValue = (event, newValue) => {
        setTabValue(newValue);
    }

    const handleOpenImageModal = (url) => {
        setImageUrl(url);
        setOpenImageModal(true);
    }

    // Transforms the date to time passed since the modification's date
    // e.g. 2 days ago, 3 weeks ago, etc.
    const formatDate = (date) => {
        const now = new Date();

        const changeDate = new Date(date);
        const diffMs = now - changeDate;
        const diffMins = Math.floor(diffMs / (1000 * 60));
        const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        const diffWeeks = Math.floor(diffDays / 7);
        const diffMonths = Math.floor(diffDays / 30);

        if (diffMs < 60000) return `${Math.floor(diffMs / 1000)} seconds ago`;
        if (diffMins < 60) return `${diffMins} minutes ago`;
        if (diffHours < 24) return `${diffHours} hours ago`;
        if (diffDays < 7) return `${diffDays} days ago`;
        if (diffMonths < 1) return `${diffWeeks} weeks ago`;
        return `${diffMonths} months ago`;
    }

    const goToViewPage = (id, tab) => {
        if (tab === 0) {
            navigate(`/part-code-management/view-part-code/${id}`);
        } else {
            navigate(`/inventory-management/view-specific-item/${id}`);
        }
    }
    const dataTableTransition = {
        transition: 'all 0.8s cubic-bezier(0.4, 0, 0.2, 1)',
        opacity: avatars ? 1 : 0,
        visibility: avatars ? 'visible' : 'hidden',
        maxHeight: avatars ? '2000px' : '0px',
        marginTop: avatars ? '16px' : '0px',
        overflow: 'hidden',
        '& .MuiTableRow-root': {
            opacity: 0,
            animation: 'fadeIn 0.6s cubic-bezier(0.4, 0, 0.2, 1) forwards',
            animationDelay: 'calc(var(--row-index) * 50ms)'
        },
        '@keyframes fadeIn': {
            '0%': {
                opacity: 0,
                transform: 'translateY(10px)'
            },
            '100%': {
                opacity: 1,
                transform: 'translateY(0)'
            }
        }
    }

    // Generate dataTable rows from the changeLog data, table=1 for partCode, table=2 for inventory
    const getRows = (info, table) => {
        console.log("getRows", info);
        console.log("info.length", info.length);
        return info && info.length ? info.map((item) => {
            const isDeleted = item.isDeleted;
            return (table === 1) ? ({
                partCode: isDeleted ? (formatPartCode(item.identifier)) : (
                    <a onClick={() => goToViewPage(item.partCode._id, 0)} style={{ cursor: 'pointer' }}>
                        {formatPartCode(item.partCode)}
                    </a>),
                link: isDeleted ? "#" : item.partCode._id,
                changeType: item.type,
                date: formatDate(item.created_at),
                username: item.user.name,
                avatar: avatars[item.user._id],
                id: item._id 
            }) : ({
                partCode: isDeleted ? (formatPartCode(item.identifier)) :
                    (<a onClick={() => goToViewPage(item.inventoryItem._id, 1)} style={{ cursor: 'pointer' }}>
                        {formatPartCode(item.inventoryItem)}
                    </a>),
                link: isDeleted ? "#" : item.inventoryItem?._id,
                changeType: item.type,
                date: formatDate(item.created_at),
                username: item.user.name,
                avatar: avatars[item.user._id],
                id: item._id
            });
        }) : [];
    }

    // DataTable columns configuration
    // The columns are the same for both tables, but the data is different
    const dataTableColumns = {
        columns: [
            {
                Header: "", accessor: "avatar", width: 35, disableSortBy: true, Cell: ({ cell: { value } }) => {
                    return (
                        <MDBox
                            onClick={() => handleOpenImageModal(value)}
                            sx={{ cursor: "pointer" }}
                        >
                            <MDAvatar src={value} alt="profile-image" size="xs" shadow="sm" />
                        </MDBox>

                    )
                }
            },
            { Header: "", accessor: "username" },
            { Header: "", accessor: "partCode" },
            { Header: "", accessor: "changeType" },
            { Header: "", accessor: "date" },
        ],
    }

    return (
        <Card sx={{ width: "100%", overflow: "visible", height: "100%" }}>

            <ImageModal
                open={openImageModal}
                onClose={() => setOpenImageModal(false)}
                mediaURL={imageUrl}
            />

            {/* Header: Icon and title */}
            <MDBox display="flex">
                <MDBox
                    display="flex"
                    justifyContent="center"
                    alignItems="center"
                    width="4rem"
                    height="4rem"
                    variant="gradient"
                    bgColor="warning"
                    color="white"
                    shadow="md"
                    borderRadius="xl"
                    ml={3}
                    mt={-2}
                >
                    <Icon fontSize="medium" color="inherit">
                        published_with_changes
                    </Icon>
                </MDBox>
                <MDTypography variant="h5" sx={{ mt: 2, mb: 1, ml: 2 }}>
                    Recent Changes
                </MDTypography>

            </MDBox>

            <MDBox p={2} sx={{ overflow: "hidden", maxWidth: "100%" }}>

                {/* Tabs navigation bar */}
                <AppBar position="static">
                    <Tabs orientation={tabsOrientation} value={tabValue} onChange={handleSetTabValue}>
                        <Tab
                            label="Part Code Changes"
                            icon={
                                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                                    badge
                                </Icon>
                            }
                        />
                        <Tab
                            label="Inventory Changes"
                            icon={
                                <Icon fontSize="small" sx={{ mt: -0.25 }}>
                                    inventory_2
                                </Icon>
                            }
                        />
                    </Tabs>
                </AppBar>
                {isLoading ? (
                    <MDBox
                        p={5}
                        display="flex"
                        justifyContent="center"
                        alignItems="center"
                        width="100%"
                        height="100%"
                    >
                        <CircularProgress />
                    </MDBox>
                ) : (
                    <MDBox>
                        {/* Part Code Changes Table */}
                        {(tabValue === 0) && (
                            <MDBox sx={dataTableTransition}>
                                <DataTable
                                    entriesPerPage={{ show: false, entries: [5], defaultValue: 5 }}
                                    table={{ ...dataTableColumns, rows: partCodeTableData }}
                                    showEntriesPerPage={false}
                                    showTotalEntries={false}
                                    isSorted={false}
                                    noTablePadding
                                    noEndBorder
                                    noHeaders
                                    noToolbar
                                    tableId="recent-part-code-changes-table"
                                />
                            </MDBox>
                        )}

                        {/* Inventory Changes Table */}
                        {(tabValue === 1) && (
                            <MDBox sx={dataTableTransition}>
                                <DataTable
                                    table={{ ...dataTableColumns, rows: inventoryTableData }}
                                    entriesPerPage={{ show: false, entries: [5], defaultValue: 5 }}
                                    showEntriesPerPage={false}
                                    showTotalEntries={false}
                                    isSorted={false}
                                    noTablePadding
                                    noEndBorder
                                    noHeaders
                                    noToolbar
                                    tableId="recent-inventory-changes-table"
                                />
                            </MDBox>
                        )}
                    </MDBox>)}

            </MDBox>

        </Card>
    )
}